import React, { useState } from 'react';
import { 
  Calendar, Target, Users, TrendingUp, Gift, 
  DollarSign, Settings, Play, Pause, Edit2,
  Copy, Trash2, CheckCircle, AlertCircle,
  BarChart3, PieChart, ArrowRight, Clock
} from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';

interface Campaign {
  id: string;
  name: string;
  type: 'seasonal' | 'clearance' | 'new_product' | 'loyalty' | 'flash';
  status: 'draft' | 'scheduled' | 'active' | 'completed' | 'paused';
  startDate: Date;
  endDate: Date;
  budget: number;
  targetRevenue: number;
  targetROI: number;
  channels: string[];
  products: Product[];
  segments: CustomerSegment[];
  kpis: CampaignKPI;
  createdAt: Date;
  updatedAt: Date;
}

interface Product {
  skuId: string;
  name: string;
  category: string;
  originalPrice: number;
  campaignPrice: number;
  discountRate: number;
  inventory: number;
  expectedSales: number;
}

interface CustomerSegment {
  id: string;
  name: string;
  size: number;
  avgPurchase: number;
  responseRate: number;
  channels: string[];
}

interface CampaignKPI {
  expectedReach: number;
  expectedConversion: number;
  expectedRevenue: number;
  roi: number;
  breakEvenPoint: number;
}

const CampaignBuilder: React.FC = () => {
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [showCampaignWizard, setShowCampaignWizard] = useState(false);
  const [wizardStep, setWizardStep] = useState(1);

  // サンプルキャンペーンデータ
  const campaigns: Campaign[] = [
    {
      id: 'camp-001',
      name: '春の新生活応援キャンペーン',
      type: 'seasonal',
      status: 'active',
      startDate: new Date('2025-03-01'),
      endDate: new Date('2025-03-31'),
      budget: 5000000,
      targetRevenue: 25000000,
      targetROI: 500,
      channels: ['店舗', 'EC', 'アプリ', 'メール'],
      products: [
        {
          skuId: 'SKU001',
          name: '新生活家電セット',
          category: '家電',
          originalPrice: 59800,
          campaignPrice: 39800,
          discountRate: 33,
          inventory: 500,
          expectedSales: 300
        },
        {
          skuId: 'SKU002',
          name: '寝具3点セット',
          category: '寝具',
          originalPrice: 29800,
          campaignPrice: 19800,
          discountRate: 33,
          inventory: 800,
          expectedSales: 500
        }
      ],
      segments: [
        {
          id: 'seg-001',
          name: '新社会人',
          size: 15000,
          avgPurchase: 45000,
          responseRate: 8.5,
          channels: ['アプリ', 'メール']
        },
        {
          id: 'seg-002',
          name: '引っ越し予定者',
          size: 8000,
          avgPurchase: 68000,
          responseRate: 12.3,
          channels: ['店舗', 'EC']
        }
      ],
      kpis: {
        expectedReach: 50000,
        expectedConversion: 4500,
        expectedRevenue: 25000000,
        roi: 500,
        breakEvenPoint: 167
      },
      createdAt: new Date('2025-01-15'),
      updatedAt: new Date('2025-01-20')
    },
    {
      id: 'camp-002',
      name: 'バレンタインフェア',
      type: 'seasonal',
      status: 'scheduled',
      startDate: new Date('2025-02-01'),
      endDate: new Date('2025-02-14'),
      budget: 2000000,
      targetRevenue: 10000000,
      targetROI: 500,
      channels: ['店舗', 'EC', 'SNS'],
      products: [],
      segments: [],
      kpis: {
        expectedReach: 30000,
        expectedConversion: 2400,
        expectedRevenue: 10000000,
        roi: 500,
        breakEvenPoint: 84
      },
      createdAt: new Date('2025-01-10'),
      updatedAt: new Date('2025-01-18')
    }
  ];

  const campaignTypes = [
    { value: 'seasonal', label: '季節キャンペーン' },
    { value: 'clearance', label: 'クリアランス' },
    { value: 'new_product', label: '新商品' },
    { value: 'loyalty', label: 'ロイヤルティ' },
    { value: 'flash', label: 'フラッシュセール' }
  ];

  const channels = [
    { value: '店舗', label: '店舗' },
    { value: 'EC', label: 'ECサイト' },
    { value: 'アプリ', label: 'モバイルアプリ' },
    { value: 'メール', label: 'メール' },
    { value: 'SNS', label: 'SNS' },
    { value: 'DM', label: 'ダイレクトメール' }
  ];

  const getStatusBadge = (status: Campaign['status']) => {
    const variants = {
      draft: 'default',
      scheduled: 'warning',
      active: 'success',
      completed: 'default',
      paused: 'error'
    };
    
    const labels = {
      draft: '下書き',
      scheduled: '予約済み',
      active: '実施中',
      completed: '完了',
      paused: '一時停止'
    };

    return (
      <Badge variant={variants[status] as any}>
        {labels[status]}
      </Badge>
    );
  };

  const getCampaignTypeIcon = (type: Campaign['type']) => {
    switch(type) {
      case 'seasonal': return Calendar;
      case 'clearance': return DollarSign;
      case 'new_product': return Gift;
      case 'loyalty': return Users;
      case 'flash': return Clock;
      default: return Target;
    }
  };

  return (
    <div className="space-y-6">
      {/* アクションバー */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Input 
            placeholder="キャンペーンを検索..." 
            className="w-64"
          />
          <Select 
            options={campaignTypes}
            placeholder="タイプで絞り込み"
            className="w-48"
          />
          <Select
            options={[
              { value: 'active', label: '実施中' },
              { value: 'scheduled', label: '予約済み' },
              { value: 'draft', label: '下書き' },
              { value: 'completed', label: '完了' }
            ]}
            placeholder="ステータス"
            className="w-40"
          />
        </div>
        <Button 
          variant="primary"
          onClick={() => setShowCampaignWizard(true)}
        >
          <Target size={16} />
          <span>新規キャンペーン作成</span>
        </Button>
      </div>

      {/* キャンペーン一覧 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {campaigns.map(campaign => {
          const Icon = getCampaignTypeIcon(campaign.type);
          const progress = campaign.status === 'active' 
            ? Math.round(((new Date().getTime() - campaign.startDate.getTime()) / 
                (campaign.endDate.getTime() - campaign.startDate.getTime())) * 100)
            : 0;

          return (
            <Card key={campaign.id}>
              <div className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg" style={{ backgroundColor: colors.ui.border }}>
                      <Icon size={20} style={{ color: colors.accent.red }} />
                    </div>
                    <div>
                      <h3 className="font-semibold" style={{ color: colors.text.primary }}>
                        {campaign.name}
                      </h3>
                      <p className="text-sm" style={{ color: colors.text.secondary }}>
                        {campaign.startDate.toLocaleDateString('ja-JP')} - {campaign.endDate.toLocaleDateString('ja-JP')}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(campaign.status)}
                </div>

                {/* 進捗バー */}
                {campaign.status === 'active' && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs" style={{ color: colors.text.secondary }}>
                        進捗
                      </span>
                      <span className="text-xs font-medium" style={{ color: colors.text.primary }}>
                        {progress}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full"
                        style={{ 
                          width: `${progress}%`,
                          backgroundColor: colors.ui.success
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* KPI */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div>
                    <p className="text-xs mb-1" style={{ color: colors.text.secondary }}>
                      予算
                    </p>
                    <p className="text-sm font-semibold" style={{ color: colors.text.primary }}>
                      ¥{campaign.budget.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs mb-1" style={{ color: colors.text.secondary }}>
                      目標売上
                    </p>
                    <p className="text-sm font-semibold" style={{ color: colors.text.primary }}>
                      ¥{campaign.targetRevenue.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs mb-1" style={{ color: colors.text.secondary }}>
                      目標ROI
                    </p>
                    <p className="text-sm font-semibold" style={{ color: colors.text.primary }}>
                      {campaign.targetROI}%
                    </p>
                  </div>
                </div>

                {/* チャネル */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {campaign.channels.map(channel => (
                    <span 
                      key={channel}
                      className="px-2 py-1 text-xs rounded"
                      style={{ 
                        backgroundColor: colors.ui.border,
                        color: colors.text.secondary
                      }}
                    >
                      {channel}
                    </span>
                  ))}
                </div>

                {/* アクション */}
                <div className="flex items-center justify-between pt-3 border-t" 
                     style={{ borderColor: colors.ui.border }}>
                  <div className="flex items-center space-x-2">
                    <button className="p-1.5 hover:bg-gray-100 rounded" title="編集">
                      <Edit2 size={16} style={{ color: colors.text.secondary }} />
                    </button>
                    <button className="p-1.5 hover:bg-gray-100 rounded" title="複製">
                      <Copy size={16} style={{ color: colors.text.secondary }} />
                    </button>
                    <button className="p-1.5 hover:bg-gray-100 rounded" title="削除">
                      <Trash2 size={16} style={{ color: colors.accent.red }} />
                    </button>
                  </div>
                  <Button 
                    variant="outline" 
                    size="small"
                    onClick={() => setSelectedCampaign(campaign)}
                  >
                    詳細を見る
                    <ArrowRight size={14} className="ml-1" />
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* キャンペーン詳細モーダル */}
      {selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b" style={{ borderColor: colors.ui.border }}>
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold" style={{ color: colors.text.primary }}>
                  {selectedCampaign.name}
                </h2>
                <button 
                  onClick={() => setSelectedCampaign(null)}
                  className="p-2 hover:bg-gray-100 rounded"
                  title="閉じる"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* 基本情報 */}
              <Card>
                <div className="p-4">
                  <h3 className="font-semibold mb-4" style={{ color: colors.text.primary }}>
                    基本情報
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm mb-1" style={{ color: colors.text.secondary }}>
                        キャンペーンタイプ
                      </p>
                      <p className="font-medium" style={{ color: colors.text.primary }}>
                        {campaignTypes.find(t => t.value === selectedCampaign.type)?.label}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm mb-1" style={{ color: colors.text.secondary }}>
                        ステータス
                      </p>
                      <div className="inline-block">
                        {getStatusBadge(selectedCampaign.status)}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm mb-1" style={{ color: colors.text.secondary }}>
                        期間
                      </p>
                      <p className="font-medium" style={{ color: colors.text.primary }}>
                        {selectedCampaign.startDate.toLocaleDateString('ja-JP')} - {selectedCampaign.endDate.toLocaleDateString('ja-JP')}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm mb-1" style={{ color: colors.text.secondary }}>
                        実施チャネル
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {selectedCampaign.channels.map(channel => (
                          <Badge key={channel} variant="default">{channel}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* 目標KPI */}
              <Card>
                <div className="p-4">
                  <h3 className="font-semibold mb-4" style={{ color: colors.text.primary }}>
                    目標KPI
                  </h3>
                  <div className="grid grid-cols-5 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold" style={{ color: colors.accent.blue }}>
                        {selectedCampaign.kpis.expectedReach.toLocaleString()}
                      </p>
                      <p className="text-xs mt-1" style={{ color: colors.text.secondary }}>
                        リーチ
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold" style={{ color: colors.accent.purple }}>
                        {selectedCampaign.kpis.expectedConversion.toLocaleString()}
                      </p>
                      <p className="text-xs mt-1" style={{ color: colors.text.secondary }}>
                        コンバージョン
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold" style={{ color: colors.ui.success }}>
                        ¥{(selectedCampaign.kpis.expectedRevenue / 1000000).toFixed(1)}M
                      </p>
                      <p className="text-xs mt-1" style={{ color: colors.text.secondary }}>
                        売上
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold" style={{ color: colors.accent.orange }}>
                        {selectedCampaign.kpis.roi}%
                      </p>
                      <p className="text-xs mt-1" style={{ color: colors.text.secondary }}>
                        ROI
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold" style={{ color: colors.text.primary }}>
                        {selectedCampaign.kpis.breakEvenPoint}
                      </p>
                      <p className="text-xs mt-1" style={{ color: colors.text.secondary }}>
                        損益分岐点
                      </p>
                    </div>
                  </div>
                </div>
              </Card>

              {/* 対象商品 */}
              {selectedCampaign.products.length > 0 && (
                <Card>
                  <div className="p-4">
                    <h3 className="font-semibold mb-4" style={{ color: colors.text.primary }}>
                      対象商品
                    </h3>
                    <div className="space-y-2">
                      {selectedCampaign.products.map(product => (
                        <div key={product.skuId} 
                             className="flex items-center justify-between p-3 border rounded-lg"
                             style={{ borderColor: colors.ui.border }}>
                          <div className="flex-1">
                            <p className="font-medium" style={{ color: colors.text.primary }}>
                              {product.name}
                            </p>
                            <p className="text-sm" style={{ color: colors.text.secondary }}>
                              {product.category} | SKU: {product.skuId}
                            </p>
                          </div>
                          <div className="flex items-center space-x-4">
                            <div className="text-right">
                              <p className="text-sm line-through" style={{ color: colors.text.secondary }}>
                                ¥{product.originalPrice.toLocaleString()}
                              </p>
                              <p className="font-bold" style={{ color: colors.accent.red }}>
                                ¥{product.campaignPrice.toLocaleString()}
                              </p>
                            </div>
                            <Badge variant="success">
                              {product.discountRate}% OFF
                            </Badge>
                            <div className="text-right">
                              <p className="text-sm" style={{ color: colors.text.secondary }}>
                                在庫: {product.inventory}
                              </p>
                              <p className="text-sm font-medium" style={{ color: colors.text.primary }}>
                                予測売上: {product.expectedSales}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              )}

              {/* ターゲットセグメント */}
              {selectedCampaign.segments.length > 0 && (
                <Card>
                  <div className="p-4">
                    <h3 className="font-semibold mb-4" style={{ color: colors.text.primary }}>
                      ターゲットセグメント
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {selectedCampaign.segments.map(segment => (
                        <div key={segment.id}
                             className="p-3 border rounded-lg"
                             style={{ borderColor: colors.ui.border }}>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium" style={{ color: colors.text.primary }}>
                              {segment.name}
                            </h4>
                            <Badge variant="default">
                              {segment.size.toLocaleString()}人
                            </Badge>
                          </div>
                          <div className="space-y-1">
                            <div className="flex justify-between text-sm">
                              <span style={{ color: colors.text.secondary }}>平均購入額</span>
                              <span style={{ color: colors.text.primary }}>
                                ¥{segment.avgPurchase.toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span style={{ color: colors.text.secondary }}>反応率</span>
                              <span style={{ color: colors.ui.success }}>
                                {segment.responseRate}%
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {segment.channels.map(channel => (
                                <span key={channel}
                                      className="px-2 py-0.5 text-xs rounded"
                                      style={{ 
                                        backgroundColor: colors.ui.border,
                                        color: colors.text.secondary
                                      }}>
                                  {channel}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              )}

              {/* アクションボタン */}
              <div className="flex items-center justify-end space-x-2 pt-4 border-t"
                   style={{ borderColor: colors.ui.border }}>
                {selectedCampaign.status === 'draft' && (
                  <Button variant="primary">
                    <Play size={16} />
                    <span>キャンペーンを開始</span>
                  </Button>
                )}
                {selectedCampaign.status === 'active' && (
                  <Button variant="outline">
                    <Pause size={16} />
                    <span>一時停止</span>
                  </Button>
                )}
                <Button variant="outline">
                  <BarChart3 size={16} />
                  <span>レポートを表示</span>
                </Button>
                <Button variant="outline">
                  <Edit2 size={16} />
                  <span>編集</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* キャンペーン作成ウィザード */}
      {showCampaignWizard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full">
            <div className="p-6 border-b" style={{ borderColor: colors.ui.border }}>
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold" style={{ color: colors.text.primary }}>
                  新規キャンペーン作成
                </h2>
                <button 
                  onClick={() => {
                    setShowCampaignWizard(false);
                    setWizardStep(1);
                  }}
                  className="p-2 hover:bg-gray-100 rounded"
                  title="閉じる"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* プログレスバー */}
              <div className="flex items-center mb-6">
                {[1, 2, 3, 4].map(step => (
                  <React.Fragment key={step}>
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                      wizardStep >= step 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 text-gray-500'
                    }`}>
                      {wizardStep > step ? (
                        <CheckCircle size={16} />
                      ) : (
                        <span className="text-sm font-medium">{step}</span>
                      )}
                    </div>
                    {step < 4 && (
                      <div className={`flex-1 h-1 mx-2 ${
                        wizardStep > step ? 'bg-blue-500' : 'bg-gray-200'
                      }`} />
                    )}
                  </React.Fragment>
                ))}
              </div>

              {/* ウィザードコンテンツ */}
              <div className="min-h-[300px]">
                {wizardStep === 1 && (
                  <div className="space-y-4">
                    <h3 className="font-semibold mb-4" style={{ color: colors.text.primary }}>
                      基本情報
                    </h3>
                    <Input 
                      label="キャンペーン名"
                      placeholder="例: 春の新生活応援キャンペーン"
                    />
                    <Select 
                      label="キャンペーンタイプ"
                      options={campaignTypes}
                      placeholder="タイプを選択"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <Input 
                        label="開始日"
                        type="date"
                      />
                      <Input 
                        label="終了日"
                        type="date"
                      />
                    </div>
                  </div>
                )}

                {wizardStep === 2 && (
                  <div className="space-y-4">
                    <h3 className="font-semibold mb-4" style={{ color: colors.text.primary }}>
                      予算と目標
                    </h3>
                    <Input 
                      label="予算"
                      type="number"
                      placeholder="5000000"
                      prefix="¥"
                    />
                    <Input 
                      label="目標売上"
                      type="number"
                      placeholder="25000000"
                      prefix="¥"
                    />
                    <Input 
                      label="目標ROI"
                      type="number"
                      placeholder="500"
                      suffix="%"
                    />
                  </div>
                )}

                {wizardStep === 3 && (
                  <div className="space-y-4">
                    <h3 className="font-semibold mb-4" style={{ color: colors.text.primary }}>
                      実施チャネル
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {channels.map(channel => (
                        <label key={channel.value}
                               className="flex items-center space-x-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50"
                               style={{ borderColor: colors.ui.border }}>
                          <input type="checkbox" className="rounded" />
                          <span style={{ color: colors.text.primary }}>{channel.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {wizardStep === 4 && (
                  <div className="space-y-4">
                    <h3 className="font-semibold mb-4" style={{ color: colors.text.primary }}>
                      確認
                    </h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span style={{ color: colors.text.secondary }}>キャンペーン名</span>
                          <span style={{ color: colors.text.primary }}>春の新生活応援キャンペーン</span>
                        </div>
                        <div className="flex justify-between">
                          <span style={{ color: colors.text.secondary }}>タイプ</span>
                          <span style={{ color: colors.text.primary }}>季節キャンペーン</span>
                        </div>
                        <div className="flex justify-between">
                          <span style={{ color: colors.text.secondary }}>期間</span>
                          <span style={{ color: colors.text.primary }}>2025/03/01 - 2025/03/31</span>
                        </div>
                        <div className="flex justify-between">
                          <span style={{ color: colors.text.secondary }}>予算</span>
                          <span style={{ color: colors.text.primary }}>¥5,000,000</span>
                        </div>
                        <div className="flex justify-between">
                          <span style={{ color: colors.text.secondary }}>目標売上</span>
                          <span style={{ color: colors.text.primary }}>¥25,000,000</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center p-3 bg-green-50 rounded-lg">
                      <CheckCircle size={20} style={{ color: colors.ui.success }} />
                      <span className="ml-2 text-sm" style={{ color: colors.ui.success }}>
                        キャンペーンの作成準備が完了しました
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* ボタン */}
              <div className="flex items-center justify-between pt-4 border-t"
                   style={{ borderColor: colors.ui.border }}>
                <Button 
                  variant="outline"
                  onClick={() => {
                    if (wizardStep > 1) {
                      setWizardStep(wizardStep - 1);
                    } else {
                      setShowCampaignWizard(false);
                    }
                  }}
                >
                  {wizardStep === 1 ? 'キャンセル' : '戻る'}
                </Button>
                <Button 
                  variant="primary"
                  onClick={() => {
                    if (wizardStep < 4) {
                      setWizardStep(wizardStep + 1);
                    } else {
                      // 企画立案アクション
                      const campaign = {
                        type: 'seasonal',
                        name: '新規キャンペーン',
                        period: '1ヶ月',
                        budget: 5000000,
                        segment: '全顧客',
                        channels: ['メール', 'SNS'],
                        kpi: 500
                      };
                      console.log('🎯 企画を立案しました！', campaign);
                      const typeName = campaignTypes.find(t => t.value === campaign.type)?.label || '季節キャンペーン';
                      alert(`✅ 企画を立案しました！\n\n企画内容:\n- 名称: ${campaign.name}\n- タイプ: ${typeName}\n- 期間: ${campaign.period}\n- 予算: ¥${campaign.budget.toLocaleString()}\n- 対象: ${campaign.segment}\n- チャネル: ${campaign.channels.join(', ')}\n- 目標ROI: ${campaign.kpi}%`);
                      
                      setShowCampaignWizard(false);
                      setWizardStep(1);
                    }
                  }}
                >
                  {wizardStep === 4 ? 'キャンペーンを作成' : '次へ'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignBuilder;
